import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import Tooltip from 'rc-tooltip';
import { cloneDeep, flatten, groupBy, range } from 'lodash';
import {
  DefaultFinanceTerms,
  FinanceType,
} from '@upstart/harmony/lib/constants';
import {
  CreditRatingMax,
  MileageType,
} from '@upstart/harmony/lib/legos/constants';
import closeImage from '../../../assets/close.png';
import { DealerLaunchStepProps } from '../types';
import {
  TextField,
  TextFieldValidationState,
} from '../../Input/TextField/TextField';
import { TooltipIcon } from '../../TooltipIcon/TooltipIcon';
import styles from '../styles.module.scss';
import { Button } from '../../Input/Button/Button';
import {
  decimalValidatorFunction,
  wholeNumberValidatorFunction,
} from '../../../utils/validation';
import { useLaunchpadForm } from '../../../hooks/useLaunchpadForm';
import { DEFAULT_CREDIT_TIERS, DEFAULT_LEASE_PRICING } from '../defaults';
import {
  CreditPricingLeasePricing,
  CreditPricingLeaseTerm,
} from '@upstart/harmony/lib/legos/creditPricingType';

const wholeNumberValidationState = (
  data: string | undefined,
): TextFieldValidationState => {
  const wholeNumberMessage = wholeNumberValidatorFunction(data);
  return wholeNumberMessage
    ? {
        valid: false as const,
        message: 'This field is required',
      }
    : { valid: true };
};
const mileageValidationState = (
  data: string | undefined,
  isUndefinedValid = true,
  terms: TermForm[] = [],
) => {
  const groups = groupBy(terms, (t) => t.months);
  if (Object.values(groups)[0].filter((t) => t.mileage === data).length > 1) {
    return {
      valid: false as const,
      message: 'Duplicate Mileage',
    };
  }

  if (isUndefinedValid && !data) {
    return { valid: true as const };
  }
  return wholeNumberValidationState(data);
};
const termValidationState = (
  data: string | undefined,
  isUndefinedValid = true,
  terms: TermForm[] = [],
  numTerms = 0,
) => {
  const groups = groupBy(terms, (t) => t.months);

  const mileagesPerTerm = terms.length / numTerms;
  if (
    Object.values(groups).some((values) => values.length !== mileagesPerTerm)
  ) {
    return {
      valid: false as const,
      message: 'Duplicate Term',
    };
  }

  if (isUndefinedValid && !data) {
    return { valid: true as const };
  }
  return wholeNumberValidationState(data);
};

const decimalNumberValidation = (
  data: string | undefined,
  isUndefinedValid = true,
): TextFieldValidationState => {
  if (isUndefinedValid && !data) {
    return { valid: true };
  }
  const decimalMessage = decimalValidatorFunction(data);
  return decimalMessage
    ? {
        valid: false,
        message: decimalMessage,
      }
    : { valid: true };
};

const validateTerms = (terms: TermForm[], numTerms: number) => {
  return !terms.some(
    (t) =>
      !termValidationState(t.months, false, terms, numTerms).valid ||
      !mileageValidationState(t.mileage, false, terms).valid,
  );
};

const validatePricing = (pricing: { moneyFactor?: string }[][]) => {
  return !pricing.some((row) =>
    row.some((p) => {
      return !decimalNumberValidation(p?.moneyFactor, false).valid;
    }),
  );
};

type TermForm = {
  months?: string;
  mileage?: string;
};

export const DealerLaunchPricingRatesLease: FunctionComponent<DealerLaunchStepProps> = ({
  dealer,
  prevStep,
  nextStep,
}) => {
  const creditTiers =
    dealer.launchpad?.creditPricing?.[FinanceType.LEASE]?.creditTiers ||
    DEFAULT_CREDIT_TIERS;

  const defaultTerms = useMemo(
    () =>
      dealer.launchpad?.creditPricing?.[FinanceType.LEASE]?.terms?.map((t) => ({
        months: t.months.toString(),
        mileage: t.mileage.toString(),
      })) ||
      (flatten(
        DefaultFinanceTerms.LEASE.map((months) =>
          Object.values(MileageType).map((mileage) => ({
            months: months.toString(),
            mileage: mileage.toString(),
          })),
        ),
      ) as TermForm[]),
    [dealer],
  );

  const [numberOfTerms, setNumberOfTerms] = useState(
    () => new Set(defaultTerms.map((m) => m.months)).size,
  );
  const defaultPricing = useMemo(() => {
    const numMiles = defaultTerms.length / numberOfTerms;
    const savedPricing = (
      dealer.launchpad?.creditPricing?.[FinanceType.LEASE]?.pricing ||
      DEFAULT_LEASE_PRICING
    ).map(
      (row) =>
        row
          .map((p, mileIdx) =>
            mileIdx < numMiles * numberOfTerms
              ? { moneyFactor: p.moneyFactor.toString() }
              : undefined,
          )
          .filter((pricing) => !!pricing) as { moneyFactor: string }[],
    );

    if (savedPricing?.length !== undefined) {
      while (savedPricing.length < creditTiers.length) {
        savedPricing.push(
          new Array(defaultTerms.length).fill(0).map(() => ({
            moneyFactor: '',
          })),
        );
      }
      while (savedPricing.length > creditTiers.length) {
        savedPricing.pop();
      }
    }
    return savedPricing;
  }, [creditTiers, defaultTerms, numberOfTerms, dealer]);

  const form = useLaunchpadForm({
    defaultValues: {
      terms: defaultTerms,
      pricing: defaultPricing,
    },
  });
  const termArray = form.watch('terms');
  useEffect(() => {
    form.register('terms');
    form.register('pricing');
  }, [form]);

  const pricing = form.getValues('pricing') || defaultPricing;

  const [selectedTermIdx, setTermIdx] = useState(0);

  const numMileages = termArray.length / numberOfTerms;

  const addTerm = () => {
    creditTiers?.forEach((_, tierIdx) => {
      pricing[tierIdx] = pricing[tierIdx].concat(
        range(0, numMileages).map(() => ({ moneyFactor: '' })),
      );
    });
    form.setValue('pricing', [...pricing], {
      shouldValidate: true,
      shouldDirty: true,
    });
    form.setValue(
      'terms',
      termArray.concat(
        termArray.slice(0, numMileages).map(({ mileage }) => ({
          months: '',
          mileage,
        })),
      ) as TermForm[],
    );
    setNumberOfTerms(numberOfTerms + 1);
    setTermIdx(numberOfTerms);
  };

  const removeTerm = (idx: number) => {
    creditTiers?.forEach((_, tierIdx) => {
      pricing[tierIdx] = [
        ...pricing[tierIdx].slice(0, numMileages * idx),
        ...pricing[tierIdx].slice(numMileages * (idx + 1), termArray.length),
      ];
    });

    form.setValue('terms', [
      ...termArray.slice(0, numMileages * idx),
      ...termArray.slice(numMileages * (idx + 1), termArray.length),
    ] as TermForm[]);
    form.setValue('pricing', [...pricing], {
      shouldValidate: true,
      shouldDirty: true,
    });
    setTermIdx(0);
    setNumberOfTerms(numberOfTerms - 1);
  };

  const setTermMonths = (months: string) => {
    const clonedTerm = cloneDeep(termArray);
    for (let i = 0; i < numMileages; i += 1) {
      clonedTerm[selectedTermIdx * numMileages + i].months = months;
    }
    form.setValue('terms', clonedTerm as TermForm[]);
  };

  const addAnotherMileage = () => {
    const clonedTerm = cloneDeep(termArray);

    for (let i = 0; i < numberOfTerms; i += 1) {
      const { months } = clonedTerm[i * numMileages + i];
      clonedTerm.splice((i + 1) * numMileages + i, 0, {
        months,
        mileage: '',
      });
    }
    creditTiers?.forEach((_, tierIdx) => {
      for (let i = 0; i < numberOfTerms; i += 1) {
        pricing[tierIdx].splice((i + 1) * numMileages + i, 0, {
          moneyFactor: '',
        });
      }
    });
    form.setValue('pricing', [...pricing], {
      shouldValidate: true,
      shouldDirty: true,
    });
    form.setValue('terms', clonedTerm as TermForm[], {
      shouldValidate: true,
      shouldDirty: true,
    });
  };
  const removeMileage = (mileageIdx: number) => {
    const clonedTerm = cloneDeep(termArray);
    for (let i = 0; i < numberOfTerms; i += 1) {
      clonedTerm.splice(i * numMileages + mileageIdx - i, 1);
    }
    creditTiers?.forEach((_, tierIdx) => {
      for (let i = 0; i < numberOfTerms; i += 1) {
        pricing[tierIdx].splice(i * numMileages + mileageIdx - i, 1);
      }
    });
    form.setValue('pricing', [...pricing], {
      shouldValidate: true,
      shouldDirty: true,
    });
    form.setValue('terms', [...clonedTerm] as TermForm[], {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleMileageChange = (mileageIdx: number, value: string) => {
    const clonedTerm = cloneDeep(termArray);
    for (let i = 0; i < numberOfTerms; i += 1) {
      clonedTerm[i * numMileages + mileageIdx].mileage = value;
    }
    form.setValue('terms', [...clonedTerm] as TermForm[], {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handlePricingChange = (
    creditIdx: number,
    mileageIdx: number,
    value: string,
  ) => {
    pricing[creditIdx][
      selectedTermIdx * numMileages + mileageIdx
    ].moneyFactor = value;
    form.setValue('pricing', [...pricing], {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const onSubmit = async (data: { pricing: { moneyFactor?: string }[][] }) => {
    if (!validateTerms(termArray as TermForm[], numberOfTerms)) {
      return;
    }
    if (!validatePricing(pricing)) {
      return;
    }

    const creditPricingForLease = {
      pricing: data.pricing.map((row) =>
        row.map((i) => ({
          moneyFactor: parseFloat(i.moneyFactor || '0'),
          onePayLeaseMoneyFactor: 0,
        })),
      ) as CreditPricingLeasePricing[][],
      terms: termArray.map((t) => ({
        months: parseInt(t.months || '0', 10),
        mileage: parseInt(t.mileage || '0', 10),
      })) as CreditPricingLeaseTerm[],
    };

    // overwrite all the apr configs for different client types and conditions
    // We default them all to be enabled if pricing rates is set
    await nextStep({
      launchpad: {
        creditPricing: {
          [FinanceType.LEASE]: {
            ...creditPricingForLease,
            creditTiers,
          },
        },
      },
    });
  };

  const renderMileageHeader = (mileageIdx: number) => {
    const mileageValue = termArray[mileageIdx].mileage;
    const state = mileageValidationState(mileageValue, false, termArray);
    return (
      <div
        key={mileageIdx}
        className={styles.ratesHeader}
        style={{ flex: 2, position: 'relative' }}
      >
        {mileageIdx > 2 && (
          <div className={classNames(styles.closeTopRight)}>
            <Button
              key={`grid-close=${mileageIdx}`}
              onClick={() => removeMileage(mileageIdx)}
              variant="minimal"
            >
              <Tooltip
                placement="top"
                overlay={`Deletes mileage option ${
                  mileageValue ? `(${mileageValue})` : ''
                } for all terms`}
              >
                <img src={closeImage} alt="delete" />
              </Tooltip>
            </Button>
          </div>
        )}
        <div className={styles.mileageHeader}>
          <TextField
            labelClassName={styles.label}
            label={`Mileage ${
              ['One', 'Two', 'Three', 'Four', 'Five'][mileageIdx]
            }`}
            name={`mileage${mileageIdx}`}
            dataTestId={`mileage${mileageIdx}`}
            addOn="Miles"
            width="sm"
            value={mileageValue}
            validationState={state}
            onChange={(e) => {
              handleMileageChange(mileageIdx, e.target.value);
            }}
          />
        </div>
      </div>
    );
  };
  const renderMileageRatingRow = (creditIdx: number) => {
    const high =
      creditIdx === 0
        ? CreditRatingMax
        : (creditTiers?.[creditIdx - 1].value || 1) - 1;

    return (
      <div key={creditIdx} style={{ display: 'flex', alignItems: 'center' }}>
        <div
          className={classNames(styles.ratesInputCell, styles.beginCell)}
          style={{ flex: 1 }}
        >
          <span className={styles.tooltipLabel}>
            Tier {creditIdx + 1} Markup
          </span>
          <TooltipIcon
            content={`Credit Range ${creditTiers?.[creditIdx].value} - ${high}`}
          />
        </div>
        {range(0, numMileages).map((mileageIdx) => {
          const moneyFactor =
            pricing?.[creditIdx][selectedTermIdx * numMileages + mileageIdx]
              .moneyFactor;
          const mileageRateValidationState = decimalNumberValidation(
            moneyFactor,
            false,
          );
          return (
            <div
              key={mileageIdx}
              style={{ flex: 2 }}
              className={classNames(
                styles.ratesInputCell,
                styles.tier,
                creditIdx === 0 && styles.top,
                mileageIdx === numMileages - 1 && styles.right,
              )}
            >
              <TextField
                skipFormRegistration
                className={classNames(styles.rateInput)}
                labelClassName={styles.rateInputLabel}
                iconClassName={styles.rateInputIcon}
                label="Mark Up"
                title="Enter a mileage value to edit this field."
                dataTestId={`mileage-markup-${creditIdx}-${mileageIdx}`}
                name={`mileage-markup-${creditIdx}-${mileageIdx}`}
                width="sm"
                value={moneyFactor}
                validationState={mileageRateValidationState}
                onChange={(e: any) => {
                  handlePricingChange(creditIdx, mileageIdx, e.target.value);
                }}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderTermRatesGrid = () => {
    const grid = termArray[selectedTermIdx * numMileages];
    const wholeNumberValidation: TextFieldValidationState = termValidationState(
      grid.months,
      false,
      termArray,
      numberOfTerms,
    );

    return (
      <div>
        <div>
          <TextField
            label={`Term Option ${
              ['One', 'Two', 'Three', 'Four', 'Five'][selectedTermIdx]
            }`}
            name={`grid-${selectedTermIdx}-term`}
            addOn="Months"
            width="sm"
            value={grid.months}
            validationState={wholeNumberValidation}
            onChange={(e) => {
              setTermMonths(e.target.value);
            }}
          />
        </div>
        <h1>Set the money factor markup for lease rates</h1>
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <div className={styles.ratesGrid} style={{ display: 'block' }}>
            <div style={{ display: 'flex' }}>
              <div style={{ flex: 1 }} />
              {range(0, numMileages).map(renderMileageHeader)}
            </div>
            {range(0, creditTiers?.length || 0).map(renderMileageRatingRow)}
          </div>
          {numMileages < 5 && (
            <div style={{ marginLeft: 20, marginTop: 20 }}>
              <Button
                className={classNames(styles.pageNavButton)}
                label="+ Add Mileage"
                onClick={() => addAnotherMileage()}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  // we render a tab for each grid lease term
  // we have option to close tab with each tab option
  // active tab is highlighed in primary color
  const renderTabs = () => (
    <div className={styles.termOptions}>
      {range(0, numberOfTerms).map((index) => {
        return (
          <div
            key={`grid-container-${index}`}
            data-testid={`lease-term-${index + 1}`}
            className={classNames(
              styles.option,
              index === selectedTermIdx && styles.active,
            )}
          >
            {index !== 0 && (
              <Button
                key={`grid-close=${index}`}
                onClick={() => removeTerm(index)}
                variant="minimal"
                className={classNames(styles.closeTopLeft)}
              >
                <img src={closeImage} alt="delete" />
              </Button>
            )}
            <Button
              key={`grid-active-index-${index}`}
              dataTestId={`grid-rating-tab-${index}`}
              className={styles.optionButton}
              onClick={() => {
                setTermIdx(index);
              }}
            >
              {`Term ${index + 1}`}
            </Button>
          </div>
        );
      })}
      {numberOfTerms < 5 && (
        <div className={classNames(styles.option)}>
          <Button
            dataTestId="add-another-term"
            className={classNames(styles.optionButton, styles.optionAddButton)}
            onClick={() => addTerm()}
          >
            + Add Another Term
          </Button>
        </div>
      )}
    </div>
  );

  const renderTerms = () => {
    return (
      <div className={classNames(styles.leaseRatesContainer, 'flex-column')}>
        {renderTabs()}
        {renderTermRatesGrid()}
      </div>
    );
  };

  return (
    <>
      <div className={classNames(styles.stepContainer)}>
        <div className={classNames(styles.stepTitle)}>
          Now, let&apos;s look at your lease rate configuration
        </div>
        <div className={classNames(styles.stepSubtitle)}>
          The money factor markup rate impacts the monthly payment for lease
          customers. We&apos;ve already filled in suggested rates and terms, but
          you can always edit it to suit your needs.
        </div>
        <form
          className={classNames(styles.stepCard, styles.pricingRatesStep)}
          onSubmit={form.handleSubmit((update) => onSubmit(update))}
        >
          {renderTerms()}
          <div className={styles.pageNav}>
            {prevStep && (
              <Button
                className={styles.pageNavButton}
                label="Previous"
                onClick={prevStep}
              />
            )}
            <div className="spacer" />
            <Button
              className={styles.pageNavButton}
              label="Continue"
              type="submit"
              variant="primary"
            />
          </div>
        </form>
      </div>
    </>
  );
};
